import type {
  ActivationCodeResult,
  ActivityItem,
  AdminAccountSummary,
  AdminDeviceOverview,
  AdminPaginatedResult,
  AdminApi,
  AdminUser,
  AuthSession,
  CreateWarungInput,
  DashboardData,
  GoogleLoginInput,
  HealthStatus,
  ListAccountsQuery,
  ListActivitiesQuery,
  ListAdminDevicesQuery,
  ListWarungsQuery,
  LoginInput,
  ProgramSettings,
  ResetAccountAccessResult,
  SubscriptionSummary,
  SyncOverview,
  UpdateSubscriptionInput,
  UpdateWarungInput,
  WarungDetail,
  WarungSummary,
  WebAccessInput,
  WebAccessResult,
} from '@/types/admin'
import { readAdminToken } from './authToken'
import { HttpClient } from './http'

interface RawPaginationMeta {
  readonly page: number
  readonly per_page: number
  readonly total: number
  readonly total_pages: number
}

interface RawPage<T> {
  readonly items: readonly T[]
  readonly meta: RawPaginationMeta
}

function paged<T>(value: RawPage<T>): AdminPaginatedResult<T> {
  return {
    items: value.items,
    meta: { page: value.meta.page, perPage: value.meta.per_page, total: value.meta.total, totalPages: value.meta.total_pages },
  }
}

function warungQueryString(query?: ListWarungsQuery): string {
  if (query === undefined) return ''
  const params = new URLSearchParams()
  if (query.search !== undefined && query.search.trim().length > 0) params.set('search', query.search.trim())
  if (query.status !== undefined && query.status !== 'all') params.set('status', query.status)
  if (query.page !== undefined) params.set('page', String(query.page))
  if (query.perPage !== undefined) params.set('per_page', String(query.perPage))
  const value = params.toString()
  return value.length > 0 ? `?${value}` : ''
}

function activityQueryString(query?: ListActivitiesQuery): string {
  if (query === undefined) return ''
  const params = new URLSearchParams()
  if (query.kind !== undefined && query.kind !== 'all') params.set('kind', query.kind)
  if (query.page !== undefined) params.set('page', String(query.page))
  if (query.perPage !== undefined) params.set('per_page', String(query.perPage))
  const value = params.toString()
  return value.length > 0 ? `?${value}` : ''
}


function adminListQueryString(query?: ListAccountsQuery | ListAdminDevicesQuery): string {
  if (query === undefined) return ''
  const params = new URLSearchParams()
  if (query.search !== undefined && query.search.trim().length > 0) params.set('search', query.search.trim())
  if (query.status !== undefined && query.status !== 'all') params.set('status', query.status)
  if ('auth' in query && query.auth !== undefined && query.auth !== 'all') params.set('auth', query.auth)
  if ('plan' in query && query.plan !== undefined && query.plan !== 'all') params.set('plan', query.plan)
  if (query.page !== undefined) params.set('page', String(query.page))
  if (query.perPage !== undefined) params.set('per_page', String(query.perPage))
  const value = params.toString()
  return value.length > 0 ? `?${value}` : ''
}

export class RemoteAdminApi implements AdminApi {
  private readonly http: HttpClient

  public constructor(baseUrl: string) {
    this.http = new HttpClient(baseUrl.replace(/\/$/, ''), readAdminToken)
  }

  public login(input: LoginInput): Promise<AuthSession> {
    return this.http.post<AuthSession>('/admin/auth/login', input, { authenticated: false })
  }

  public loginWithGoogle(input: GoogleLoginInput): Promise<AuthSession> {
    return this.http.post<AuthSession>('/admin/auth/google', input, { authenticated: false })
  }

  public me(): Promise<AdminUser> {
    return this.http.get<AdminUser>('/admin/auth/me')
  }

  public async logout(): Promise<void> {
    await this.http.post<{ readonly loggedOut: boolean }>('/admin/auth/logout', {})
  }

  public health(): Promise<HealthStatus> {
    return this.http.get<HealthStatus>('/health', { authenticated: false })
  }

  public getDashboard(): Promise<DashboardData> {
    return this.http.get<DashboardData>('/admin/dashboard')
  }

  public async listWarungs(query?: ListWarungsQuery): Promise<AdminPaginatedResult<WarungSummary>> {
    return paged(await this.http.get<RawPage<WarungSummary>>(`/admin/warungs${warungQueryString(query)}`))
  }

  public async listAccounts(query?: ListAccountsQuery): Promise<AdminPaginatedResult<AdminAccountSummary>> {
    return paged(await this.http.get<RawPage<AdminAccountSummary>>(`/admin/accounts${adminListQueryString(query)}`))
  }

  public setAccountActive(id: string, active: boolean): Promise<{ readonly updated: boolean; readonly active: boolean }> {
    return this.http.patch<{ readonly updated: boolean; readonly active: boolean }>(`/admin/accounts/${encodeURIComponent(id)}/status`, { active })
  }

  public revokeAccountSessions(id: string): Promise<{ readonly revoked: boolean }> {
    return this.http.post<{ readonly revoked: boolean }>(`/admin/accounts/${encodeURIComponent(id)}/revoke-sessions`, {})
  }

  public resetAccountAccess(id: string): Promise<ResetAccountAccessResult> {
    return this.http.post<ResetAccountAccessResult>(`/admin/accounts/${encodeURIComponent(id)}/reset-access`, {})
  }

  public async listDevices(query?: ListAdminDevicesQuery): Promise<AdminPaginatedResult<AdminDeviceOverview>> {
    return paged(await this.http.get<RawPage<AdminDeviceOverview>>(`/admin/devices${adminListQueryString(query)}`))
  }

  public revokeDevice(id: string): Promise<{ readonly revoked: boolean }> {
    return this.http.post<{ readonly revoked: boolean }>(`/admin/devices/${encodeURIComponent(id)}/revoke`, {})
  }

  public getWarung(id: string): Promise<WarungDetail> {
    return this.http.get<WarungDetail>(`/admin/warungs/${encodeURIComponent(id)}`)
  }

  public createWarung(input: CreateWarungInput): Promise<WarungDetail> {
    return this.http.post<WarungDetail>('/admin/warungs', input)
  }

  public updateWarung(id: string, input: UpdateWarungInput): Promise<WarungDetail> {
    return this.http.patch<WarungDetail>(`/admin/warungs/${encodeURIComponent(id)}`, input)
  }

  public generateActivationCode(id: string): Promise<ActivationCodeResult> {
    return this.http.post<ActivationCodeResult>(`/admin/warungs/${encodeURIComponent(id)}/activation-code`, {})
  }

  public generateWebAccess(id: string, input: WebAccessInput = {}): Promise<WebAccessResult> {
    return this.http.post<WebAccessResult>(`/admin/warungs/${encodeURIComponent(id)}/web-access`, input)
  }

  public updateSubscription(id: string, input: UpdateSubscriptionInput): Promise<SubscriptionSummary> {
    return this.http.patch<SubscriptionSummary>(`/admin/warungs/${encodeURIComponent(id)}/subscription`, input)
  }

  public getSyncOverview(): Promise<SyncOverview> {
    return this.http.get<SyncOverview>('/admin/sync')
  }

  public retrySync(warungId?: string): Promise<SyncOverview> {
    return this.http.post<SyncOverview>('/admin/sync/retry', warungId === undefined ? {} : { warungId })
  }

  public getSettings(): Promise<ProgramSettings> {
    return this.http.get<ProgramSettings>('/admin/settings')
  }

  public saveSettings(input: ProgramSettings): Promise<ProgramSettings> {
    return this.http.patch<ProgramSettings>('/admin/settings', input)
  }

  public async listActivities(query?: ListActivitiesQuery): Promise<AdminPaginatedResult<ActivityItem>> {
    return paged(await this.http.get<RawPage<ActivityItem>>(`/admin/activities${activityQueryString(query)}`))
  }
}
